import { Link } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { ActivityIcon, CheckCircle2Icon, FolderKanbanIcon, HashIcon, MailIcon, MessageSquareIcon, SettingsIcon, TicketIcon, UserIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import { AccountActivityRow } from "@/features/account/components/AccountActivityRow";
import { AccountMembershipRow } from "@/features/account/components/AccountMembershipRow";
import { ProfileIdentityCard, ProfileIdentityCardSkeleton } from "@/features/account/components/ProfileIdentityCard";
import { getMyAccountOverview } from "@/features/account/services/account";
import { AccountOverview } from "@/features/account/types";
import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { InlineError } from "@/shared/components/InlineError";
import { StatCard, StatCardSkeleton } from "@/shared/components/StatCard";
import { useErrorModal } from "@/shared/hooks/useErrorModal";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { getMyDetails } from "@/shared/lib/services/user";
import { User } from "@/shared/lib/types/user";
import { AppError } from "@/shared/lib/utils/errors";

export default function Profile() {
  const {
    data: user,
    isPending: isUserPending,
    error: userError,
    refetch: refetchUser,
  } = useQuery<User, AppError | Error>({
    queryKey: [QUERY_KEYS.GET_MY_DETAILS],
    queryFn: getMyDetails,
    staleTime: QUERY_EXPIRATIONS.GET_MY_DETAILS,
  });

  const {
    data: overview,
    isPending: isOverviewPending,
    error: overviewError,
    refetch: refetchOverview,
  } = useQuery<AccountOverview, AppError | Error>({
    queryKey: [QUERY_KEYS.GET_MY_ACCOUNT_OVERVIEW],
    queryFn: getMyAccountOverview,
    staleTime: QUERY_EXPIRATIONS.GET_MY_ACCOUNT_OVERVIEW,
  });

  const { open: userErrorModalOpen, setOpen: setUserErrorModalOpen, handleRetry: handleUserRetry } = useErrorModal(userError, refetchUser);
  const { open: overviewErrorModalOpen, setOpen: setOverviewErrorModalOpen, handleRetry: handleOverviewRetry } = useErrorModal(overviewError, refetchOverview);

  return (
    <div className="flex flex-col gap-6">
      {/* Identity header */}
      {isUserPending || userError || !user ? <ProfileIdentityCardSkeleton /> : <ProfileIdentityCard user={user} overview={overview} />}

      {userError && <Error variant="modal" title="Failed to load your profile" error={userError} open={userErrorModalOpen} onOpenChange={setUserErrorModalOpen} onRetry={handleUserRetry} />}
      {overviewError && <Error variant="modal" title="Failed to load your activity" error={overviewError} open={overviewErrorModalOpen} onOpenChange={setOverviewErrorModalOpen} onRetry={handleOverviewRetry} />}

      {/* Contribution stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {isOverviewPending || !overview ? (
          Array.from({ length: 5 }).map((_, index) => <StatCardSkeleton key={index} />)
        ) : (
          <>
            <StatCard label="Projects" value={overview.stats.projectCount} icon={FolderKanbanIcon} iconClass="text-primary" bgClass="bg-primary/10" />
            <StatCard label="Tickets raised" value={overview.stats.ticketsCreated} icon={TicketIcon} iconClass="text-primary" bgClass="bg-primary/10" />
            <StatCard label="Assigned to me" value={overview.stats.ticketsAssigned} icon={UserIcon} iconClass="text-amber-600 dark:text-amber-400" bgClass="bg-amber-500/10" />
            <StatCard label="Resolved" value={overview.stats.ticketsResolved} icon={CheckCircle2Icon} iconClass="text-emerald-600 dark:text-emerald-400" bgClass="bg-emerald-500/10" />
            <StatCard label="Comments" value={overview.stats.commentCount} icon={MessageSquareIcon} iconClass="text-muted-foreground" bgClass="bg-muted" />
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
        {/* Project memberships */}
        <Card className="gap-0 p-0">
          <CardHeader className="border-b px-6 py-4">
            <div className="flex items-center gap-2">
              <FolderKanbanIcon className="size-4 text-primary" />
              <CardTitle>Project memberships</CardTitle>
            </div>
            <CardDescription>The projects you belong to and the role you hold in each.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {overviewError && !overviewErrorModalOpen ? (
              <InlineError title="Failed to load memberships" description="Something went wrong while fetching your projects." onRetry={handleOverviewRetry} />
            ) : isOverviewPending || !overview ? (
              <div className="flex flex-col gap-4 p-6">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Skeleton key={index} className="h-10 w-full" />
                ))}
              </div>
            ) : overview.memberships.length === 0 ? (
              <EmptyState
                icon={FolderKanbanIcon}
                title="No project memberships yet"
                description="Once you join or create a project it will be listed here."
                action={
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/projects">Browse projects</Link>
                  </Button>
                }
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Project</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Open tickets</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overview.memberships.map((membership) => (
                    <AccountMembershipRow key={membership.projectId} membership={membership} />
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Account details */}
        <Card className="gap-0 p-0">
          <CardHeader className="border-b px-6 py-4">
            <div className="flex items-center gap-2">
              <UserIcon className="size-4 text-primary" />
              <CardTitle>Account details</CardTitle>
            </div>
            <CardDescription>Manage these in settings.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="flex flex-col divide-y">
              <li className="flex items-center justify-between gap-4 px-6 py-3">
                <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                  <UserIcon className="size-3.5 shrink-0" />
                  Name
                </div>
                {isUserPending || !user ? <Skeleton className="h-3 w-28" /> : <span className="text-right text-xs font-medium">{[user.firstName, user.lastName].join(" ")}</span>}
              </li>
              <li className="flex items-center justify-between gap-4 px-6 py-3">
                <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                  <MailIcon className="size-3.5 shrink-0" />
                  Email
                </div>
                {isUserPending || !user ? <Skeleton className="h-3 w-36" /> : <span className="text-right text-xs font-medium break-all">{user.emailAddress}</span>}
              </li>
              <li className="flex items-center justify-between gap-4 px-6 py-3">
                <div className="flex items-center gap-2.5 text-xs text-muted-foreground">
                  <HashIcon className="size-3.5 shrink-0" />
                  User ID
                </div>
                {isUserPending || !user ? <Skeleton className="h-3 w-32" /> : <code className="bg-muted px-1.5 py-0.5 font-mono text-[11px]">{user.id}</code>}
              </li>
            </ul>
            <div className="border-t px-6 py-4">
              <Button variant="outline" size="sm" className="w-full" asChild>
                <Link to="/settings">
                  <SettingsIcon />
                  Manage account settings
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent activity */}
      <Card className="gap-0 p-0">
        <CardHeader className="border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <ActivityIcon className="size-4 text-primary" />
            <CardTitle>Recent activity</CardTitle>
          </div>
          <CardDescription>Your latest contributions across every project you belong to.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {overviewError && !overviewErrorModalOpen ? (
            <InlineError title="Failed to load activity" description="Something went wrong while fetching your recent activity." onRetry={handleOverviewRetry} />
          ) : isOverviewPending || !overview ? (
            <div className="flex flex-col gap-4 p-6">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          ) : overview.recentActivity.length === 0 ? (
            <EmptyState icon={ActivityIcon} title="No activity yet" description="Tickets you raise, resolve or comment on will appear here." />
          ) : (
            <div className="flex flex-col divide-y">
              {overview.recentActivity.map((activity) => (
                <AccountActivityRow key={activity.id} activity={activity} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
